import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';

import { BORDER_RADIUS, semanticColors } from '@/theme/tokens';

interface SaveBallotPromptProps {
  /** The ballot's election has been held — the copy talks about keeping a record. */
  isPast?: boolean;
  saving: boolean;
  onSave: () => void;
}

/**
 * Asks a signed-in voter to save a draft ballot whose picks live only on this
 * device. Draft picks reach the account only on save, so an unsaved ballot loses
 * them on sign-out or on another device. Mirrors the mobile app's SaveBallotPrompt.
 */
export default function SaveBallotPrompt({ isPast, saving, onSave }: SaveBallotPromptProps) {
  const { t } = useTranslation('ballot');
  const body = isPast ? t('records.savePromptBodyPast') : t('records.savePromptBody');

  return (
    <Box
      component="section"
      aria-labelledby="save-ballot-prompt-title"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        mx: 1,
        px: 1.5,
        py: 1.25,
        backgroundColor: semanticColors.themeAqua,
        border: `1px solid ${semanticColors.ballotElectionCard}`,
        borderRadius: BORDER_RADIUS,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <SaveOutlinedIcon
          fontSize="small"
          aria-hidden="true"
          sx={{ color: semanticColors.ballotElectionCard, mt: '1px' }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography
            id="save-ballot-prompt-title"
            component="h2"
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            {t('records.savePromptTitle')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.primary', lineHeight: 1.45 }}>
            {body}
          </Typography>
        </Box>
      </Box>
      {/* Stays enabled while saving (aria-disabled + a guarded handler) so it keeps
          focus and its dark fill, which the spinner needs to stay visible. */}
      <Button
        variant="contained"
        disableElevation
        onClick={() => {
          if (!saving) onSave();
        }}
        aria-disabled={saving}
        aria-busy={saving}
        sx={{
          alignSelf: 'flex-start',
          minHeight: 44,
          minWidth: 120,
          px: 2,
          borderRadius: 999,
          textTransform: 'none',
          fontWeight: 600,
          backgroundColor: semanticColors.textPrimary,
          color: semanticColors.textOnPrimary,
          '&:hover': { backgroundColor: semanticColors.textPrimary, opacity: 0.88 },
          '&[aria-disabled="true"]': { cursor: 'progress' },
        }}
      >
        {saving ? (
          <CircularProgress
            size={18}
            sx={{ color: semanticColors.textOnPrimary }}
            aria-label={t('records.saving')}
          />
        ) : (
          t('records.saveBallot')
        )}
      </Button>
      <Box component="span" role="status" sx={visuallyHidden}>
        {saving ? t('records.saving') : ''}
      </Box>
    </Box>
  );
}
